import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Clock } from "lucide-react";

const IDLE_MS = 15 * 60 * 1000;
const WARN_MS = 60 * 1000;
const EVENTS = ["mousemove", "keydown", "pointerdown", "scroll", "touchstart"];
const ACTIVE_KEY = "hc_last_active"; // shared across tabs: activity anywhere keeps every tab signed in

const sharedLast = () => { try { return Number(localStorage.getItem(ACTIVE_KEY)) || 0; } catch { return 0; } };
const writeShared = (t) => { try { localStorage.setItem(ACTIVE_KEY, String(t)); } catch { /* storage unavailable */ } };

export function IdleLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);
  const [left, setLeft] = useState(WARN_MS / 1000);
  const last = useRef(Date.now());

  useEffect(() => {
    if (!user) return;
    last.current = Date.now();
    writeShared(last.current);
    const touch = () => { last.current = Date.now(); if (last.current - sharedLast() > 5000) writeShared(last.current); };
    EVENTS.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    const t = setInterval(async () => {
      const idle = Date.now() - Math.max(last.current, sharedLast());
      if (idle >= IDLE_MS) {
        clearInterval(t);
        setWarning(false);
        await logout();
        navigate("/login");
        import("sonner").then(({ toast }) => toast("You were signed out after 15 minutes of inactivity to protect your information.", { id: "idle-logout", duration: 8000 }));
      } else if (idle >= IDLE_MS - WARN_MS) {
        setWarning(true);
        setLeft(Math.ceil((IDLE_MS - idle) / 1000));
      } else {
        setWarning(false);
      }
    }, 1000);
    return () => { clearInterval(t); EVENTS.forEach((e) => window.removeEventListener(e, touch)); };
  }, [user, logout, navigate]);

  if (!user || !warning) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4" data-testid="idle-warning">
      <div className="warm-card p-6 w-full max-w-sm text-center space-y-3">
        <Clock className="w-8 h-8 mx-auto text-amber-700" />
        <h3 className="font-display text-lg font-bold text-stone-900">Still there?</h3>
        <p className="text-sm text-stone-600">For your privacy you'll be signed out in <span className="font-mono font-bold" data-testid="idle-countdown">{left}s</span> unless you keep going.</p>
        <button data-testid="idle-stay" onClick={() => { last.current = Date.now(); writeShared(last.current); setWarning(false); }} className="amber-btn w-full text-sm">I'm still here</button>
      </div>
    </div>
  );
}
